import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Trash2 } from 'lucide-react';

const OrderLightEditModal = ({ order, erpActive = false, onClose, onSave }) => {
  const { t } = useTranslation('orders');

  const [rows, setRows] = useState(() =>
    (order.items || []).map(item => ({
      _id: item._id,
      productId: item.productId,
      name: item.name,
      sku: item.sku,
      unit: item.unit,
      unitPrice: item.unitPrice || 0,
      images: item.images || [],
      originalQuantity: item.quantity,
      newQuantity: item.quantity,
      removed: false
    }))
  );
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const setQty = (id, qty) =>
    setRows(prev => prev.map(r => r._id === id
      ? { ...r, newQuantity: Math.max(0, parseInt(qty, 10) || 0) }
      : r
    ));

  const toggleRemove = (id) =>
    setRows(prev => prev.map(r => r._id === id
      ? { ...r, removed: !r.removed }
      : r
    ));

  const subtotal = rows
    .filter(r => !r.removed)
    .reduce((s, r) => s + (r.unitPrice || 0) * (r.newQuantity || 0), 0);

  const hasChanges = rows.some(r =>
    r.removed ||
    Number(r.newQuantity) !== Number(r.originalQuantity)
  );

  const handleSave = async () => {
    if (saving || !hasChanges) return;
    setSaving(true);

    const items = rows
      .filter(r => !r.removed)
      .map(r => ({ _id: r._id, quantity: r.newQuantity }));
    const removedItemIds = rows
      .filter(r => r.removed)
      .map(r => r._id);

    try {
      await onSave({ items, removedItemIds, reason });
    } catch (err) {
      console.error('Light edit save failed:', err);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('simpleOrders.lightEdit.title', 'Edit Items')}
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600 mb-3">
            {t('simpleOrders.lightEdit.helper', 'Change quantities or remove lines. No item-level statuses are tracked here.')}
          </p>

          <div className="space-y-2">
            {rows.map(row => {
              const isRemoved = row.removed;
              return (
                <div
                  key={row._id}
                  className={`flex items-center gap-3 px-3 py-2 border rounded-lg ${
                    isRemoved ? 'bg-red-50 border-red-200 opacity-60' : 'bg-white'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{row.name}</p>
                    <p className="text-xs text-gray-500">
                      {row.sku ? `${row.sku} · ` : ''}€{(row.unitPrice || 0).toFixed(2)}{row.unit ? ` / ${row.unit}` : ''}
                    </p>
                  </div>
                  <input
                    type="number"
                    min="0"
                    value={row.newQuantity}
                    disabled={isRemoved}
                    onChange={(e) => setQty(row._id, e.target.value)}
                    className="w-20 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none disabled:bg-gray-100"
                  />
                  <button
                    type="button"
                    onClick={() => toggleRemove(row._id)}
                    className={`p-2 rounded ${
                      isRemoved
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'text-red-600 hover:bg-red-50'
                    }`}
                    title={isRemoved
                      ? t('simpleOrders.lightEdit.undoRemove', 'Undo remove')
                      : t('simpleOrders.lightEdit.removeLine', 'Remove line')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('simpleOrders.lightEdit.reason', 'Reason')}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none"
              placeholder={t('simpleOrders.lightEdit.reasonPlaceholder', 'Optional note for the audit log')}
            />
          </div>

          <div className="mt-4 flex items-center justify-between px-3 py-2 bg-gray-50 rounded">
            <span className="text-sm text-gray-600">
              {t('simpleOrders.lightEdit.newSubtotal', 'New subtotal')}
            </span>
            <span className="text-sm font-semibold text-gray-900">€{subtotal.toFixed(2)}</span>
          </div>

          {erpActive && (
            <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              {t('simpleOrders.lightEdit.erpHeadsUp', "This change won't push to SoftOne — adjust the ERP record too if needed.")}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100 disabled:opacity-50"
          >
            {t('simpleOrders.lightEdit.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="px-4 py-2 bg-sky-600 text-white rounded-lg text-sm hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving
              ? t('simpleOrders.lightEdit.saving', 'Saving...')
              : t('simpleOrders.lightEdit.save', 'Save changes')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderLightEditModal;
